import { useState } from "react";
import {
  SalesReportSummary,
  SalesReportSummaryStaff,
} from "../../../types/sale-reports.type";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";
import { STAFF_ROLE } from "../../../utils/staffRole";

const compact = "@media " + COMPACT_QUERY;

interface SalePersonCardProps {
  reportSummary: SalesReportSummary;
}

// Order deliberately matches the printed sales report (sale, spare, assistant),
// which is NOT the backend's deduction ordering (sale, assistant, spare).
const ROLE_ORDER = [
  STAFF_ROLE.SALESMAN,
  STAFF_ROLE.SPARE,
  STAFF_ROLE.ASSISTANT,
] as string[];

interface StaffRoleRow {
  role: string;
  staff: SalesReportSummaryStaff;
}

export default function SalePersonCard({ reportSummary }: SalePersonCardProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [currentRow, setCurrentRow] = useState<StaffRoleRow | null>(null);

  const handleOpen = (row: StaffRoleRow) => {
    setCurrentRow(row);
    setOpen(true);
  };

  const handleClose = () => {
    setCurrentRow(null);
    setOpen(false);
  };

  const GmRow = ({ name }: { name: string }) => (
    <Box mb={0.75}>
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{ display: "block", whiteSpace: "nowrap" }}
      >
        ผู้ตรวจสอบ:
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: (theme) => theme.palette.gray.main,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "block",
        }}
        title={name}
      >
        {name}
      </Typography>
    </Box>
  );

  const StaffRow = ({ row }: { row: StaffRoleRow }) => (
    <Box mb={0.75}>
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{ display: "block", whiteSpace: "nowrap" }}
      >
        {row.role}:
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
        <Typography
          variant="caption"
          sx={{
            color: (theme) => theme.palette.gray.main,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
          title={row.staff.staff_fullname}
        >
          {row.staff.staff_fullname}
        </Typography>
        <VisibilityOutlinedIcon
          sx={{
            fontSize: 20,
            cursor: "pointer",
            color: (theme) => theme.palette.link.main,
            flexShrink: 0,
          }}
          onClick={() => handleOpen(row)}
        />
      </Box>
    </Box>
  );

  const staffRoleRows: StaffRoleRow[] = reportSummary.staff
    .flatMap((staff) =>
      staff.responsibilities.map((role) => ({ role, staff })),
    )
    .sort((a, b) => {
      const aIndex = ROLE_ORDER.indexOf(a.role);
      const bIndex = ROLE_ORDER.indexOf(b.role);
      const aOrder = aIndex === -1 ? ROLE_ORDER.length : aIndex;
      const bOrder = bIndex === -1 ? ROLE_ORDER.length : bIndex;
      return aOrder - bOrder;
    });

  const splitIndex = Math.ceil(staffRoleRows.length / 2);
  const leftRows = staffRoleRows.slice(0, splitIndex);
  const rightRows = staffRoleRows.slice(splitIndex);

  // Only render when the GM confirmed the check-in and returned a non-empty
  // name; guards against null/undefined/"" so the text "undefined" never shows.
  const gmName = reportSummary.gm_name?.trim();

  return (
    <>
      <Card
        sx={{
          border: (theme) => `1px solid ${theme.palette.primary.main}`,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          boxShadow: "none",
          borderRadius: "12px",
        }}
      >
        <CardContent sx={{ p: 0, width: "100%", "&:last-child": { p: 0 } }}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <PersonOutlineOutlinedIcon color="primary" sx={{ fontSize: 16 }} />
            <Typography variant="caption" color="primary" fontWeight={700}>
              ผู้รับผิดชอบ
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" mt={0.5}>
            <Box flex={1} minWidth={0}>
              {leftRows.map((row, index) => (
                <StaffRow
                  key={`${row.staff.staff_id}-${row.role}-${index}`}
                  row={row}
                />
              ))}
            </Box>
            <Box
              sx={{
                width: "1px",
                mx: 2,
                flexShrink: 0,
                background: `repeating-linear-gradient(
                  to bottom,
                  transparent 0,
                  transparent 3px,
                  #AEAEAE 3px,
                  #AEAEAE 6px
                )`,
              }}
            />
            <Box flex={1} minWidth={0}>
              {rightRows.map((row, index) => (
                <StaffRow
                  key={`${row.staff.staff_id}-${row.role}-${index}`}
                  row={row}
                />
              ))}
            </Box>
          </Box>

          {gmName && (
            <Box mt={0.5}>
              <GmRow name={gmName} />
            </Box>
          )}
        </CardContent>
      </Card>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: { sx: { [compact]: { maxHeight: "94vh" } } },
        }}
      >
        <DialogTitle>{currentRow?.role}</DialogTitle>
        <DialogContent dividers>
          {currentRow?.staff.signature && (
            <Box
              component="img"
              src={currentRow.staff.signature}
              sx={{
                width: "80%",
                height: "auto",
                borderRadius: 1,
                display: "flex",
                margin: "auto",
                [compact]: { width: "60%" },
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            ปิด
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
